import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Home, LayoutGrid, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { colors } from "@/theme";
import { Logo } from "@/components/Logo";
import { SearchSuggest } from "@/components/SearchSuggest";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";

const NAV = [
  { label: "Collections", to: "/collections" },
  { label: "Gifts", to: "/gifts" },
];

function ShopMenu() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api("/categories", { auth: false }).then(setCats).catch(() => {}); }, []);
  return (
    <div style={{ position: "relative" }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to="/" data-testid="nav-shop" className="link-underline" style={{ textDecoration: "none", color: colors.onSurface, fontSize: 13.5, fontWeight: 500, letterSpacing: 1.2, textTransform: "uppercase" }}>Shop</Link>
      {open && cats.length > 0 && (
        <div data-testid="nav-shop-menu" style={{ position: "absolute", top: "100%", left: -20, paddingTop: 14, zIndex: 50 }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "18px 22px", boxShadow: "0 18px 40px rgba(0,0,0,0.08)", display: "grid", gridTemplateColumns: `repeat(${Math.min(cats.length, 4)}, minmax(140px, auto))`, gap: "18px 28px" }}>
            {cats.map((cat) => (
              <div key={cat.id}>
                <Link to={`/category/${cat.id}`} onClick={() => setOpen(false)} data-testid={`nav-cat-${cat.id}`} style={{ textDecoration: "none", color: colors.onSurface, fontWeight: 700, fontSize: 13.5 }}>{cat.name}</Link>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 7 }}>
                  {(cat.subcategories || []).map((s) => (
                    <Link key={s.id} to={`/category/${cat.id}?sub=${s.id}`} onClick={() => setOpen(false)} style={{ textDecoration: "none", color: colors.mutedText, fontSize: 13 }}>{s.name}</Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CartIcon({ onClick }) {
  const { count } = useCart();
  return (
    <button data-testid="nav-cart" onClick={onClick} aria-label="Cart" style={iconBtn}>
      <ShoppingBag size={19} strokeWidth={1.6} color={colors.onSurface} />
      {count > 0 && (
        <span
          data-testid="cart-badge"
          style={{
            position: "absolute", top: -3, right: -4, minWidth: 17, height: 17, padding: "0 4px",
            background: colors.brand, color: colors.onBrandPrimary, borderRadius: 999, fontSize: 10,
            fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

const iconBtn = {
  position: "relative", background: "none", border: "none", cursor: "pointer",
  padding: 6, display: "flex", alignItems: "center", justifyContent: "center",
};

export function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const isMobile = useIsMobile();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  if (isMobile) {
    return (
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(252,251,248,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" }}>
          <Logo size={20} />
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button data-testid="nav-search-mobile" onClick={() => navigate("/search")} style={iconBtn} aria-label="Search">
              <Search size={19} strokeWidth={1.6} color={colors.onSurface} />
            </button>
            <CartIcon onClick={() => navigate("/cart")} />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(252,251,248,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${colors.border}` }}>
      <div className="container-max" style={{ display: "flex", alignItems: "center", gap: 32, padding: "16px 24px" }}>
        <Logo size={24} />
        <nav style={{ display: "flex", gap: 28, marginLeft: 8 }}>
          <ShopMenu />
          {NAV.map((n) => {
            const active = location.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="link-underline"
                style={{
                  textDecoration: "none", color: colors.onSurface, fontSize: 13.5, fontWeight: active ? 600 : 500,
                  letterSpacing: 1.2, textTransform: "uppercase",
                }}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div style={{ marginLeft: "auto" }}>
          <SearchSuggest width={260} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button data-testid="nav-wishlist" onClick={() => navigate("/wishlist")} style={iconBtn} aria-label="Wishlist">
            <Heart size={19} strokeWidth={1.6} color={colors.onSurface} />
          </button>
          <CartIcon onClick={() => navigate("/cart")} />
          <button data-testid="nav-profile" onClick={() => navigate(user ? "/profile" : "/login")} style={iconBtn} aria-label="Profile">
            <User size={19} strokeWidth={1.6} color={colors.onSurface} />
          </button>
        </div>
      </div>
    </header>
  );
}

const TABS = [
  { label: "Shop", to: "/", icon: Home },
  { label: "Collections", to: "/collections", icon: LayoutGrid },
  { label: "Gifts", to: "/gifts", icon: Gift },
  { label: "Wishlist", to: "/wishlist", icon: Heart },
  { label: "Profile", to: "/profile", icon: User },
];

export function BottomTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  return (
    <nav
      data-testid="bottom-tabs"
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40, display: "flex",
        background: "rgba(252,251,248,0.95)", backdropFilter: "blur(14px)", borderTop: `1px solid ${colors.border}`,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {TABS.map((t) => {
        const active = location.pathname === t.to;
        const Icon = t.icon;
        const dest = t.to === "/profile" && !user ? "/login" : t.to;
        return (
          <button
            key={t.to}
            data-testid={`tab-${t.label.toLowerCase()}`}
            onClick={() => navigate(dest)}
            style={{
              flex: 1, background: "none", border: "none", cursor: "pointer", padding: "10px 0 12px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              color: active ? colors.brand : colors.mutedText,
            }}
          >
            <Icon size={21} strokeWidth={active ? 2 : 1.6} fill={active && t.icon === Heart ? colors.brand : "none"} />
            <span style={{ fontSize: 10, fontWeight: active ? 600 : 500, letterSpacing: 0.3 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
